import type { BlogListResponse, BlogPostDetail, BlogTopic } from '@mentorivo/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Blog uçlarının istemcisi — YALNIZCA SUNUCUDA çağrılır.
 *
 * ---------------------------------------------------------------------------
 * Neden istemci bileşeninden çağrılmıyor
 * ---------------------------------------------------------------------------
 * Yazılar arama motorunun okuması gereken içerik. Tarayıcıdan çekilseydi
 * sayfanın HTML'i boş gelir, metin ancak JavaScript çalıştıktan sonra dolardı.
 * Bu yüzden hem dizin hem yazı sayfası veriyi SUNUCUDA alıyor ve bileşenlere
 * prop olarak indiriyor; `lib/api.ts` sunucudan `127.0.0.1:3040`a gidiyor,
 * nginx'e ve IP kısıtına hiç uğramadan.
 *
 * ---------------------------------------------------------------------------
 * Önbellek: 60 saniye
 * ---------------------------------------------------------------------------
 * Blog içeriği dakikalık değişen bir şey değil ama admin ekranından (Faz 9)
 * düzenlenebilir olacak. Bir dakikalık pencere iki ucu da idare ediyor:
 * ziyaretçi başına bir API çağrısı yapılmıyor, düzeltme de en geç bir dakikada
 * yayına giriyor. `no-store` yazsaydık her ziyaret bir sorgu; saatlik
 * `revalidate` yazsaydık düzelttiği yazıyı göremeyen bir editör.
 */
const REVALIDATE_SECONDS = 60;

export const blogApi = {
  /**
   * Dizin. `topic` verilmiyor bilinçli: on iki kartın hepsi bir kerede geliyor
   * ve süzgeç istemcide çalışıyor — konuya basıldığında ağa çıkılmıyor,
   * kartlar `layoutId` ile yerinde kalıyor (components/blog-posts.tsx).
   */
  list: (locale: string, topic?: BlogTopic) =>
    api.get<BlogListResponse>(
      `/blog/posts?locale=${encodeURIComponent(locale)}${topic ? `&topic=${topic}` : ''}`,
      { next: { revalidate: REVALIDATE_SECONDS } },
    ),

  detail: (slug: string, locale: string) =>
    api.get<BlogPostDetail>(
      `/blog/posts/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`,
      { next: { revalidate: REVALIDATE_SECONDS } },
    ),
};

/**
 * Yazı sayfasının adresi. Tek yerde duruyor ki dizin, ilgili yazılar ve
 * komşu bağlantıları aynı biçimi kullansın.
 *
 * Dil ön eki YOK: `@/i18n/navigation`ın `Link`i onu kendisi ekliyor.
 */
export function blogPostHref(slug: string): string {
  return `/blog/${slug}`;
}

/**
 * "Bu adreste yazı yok" mu, "API çöktü" mü?
 *
 * Ayrım önemli: birincisinde `notFound()` çağrılıp 404 sayfası basılıyor,
 * ikincisinde hata YUKARI FIRLIYOR ve Next'in hata sınırına düşüyor. İkisi
 * aynı sayılsaydı API kapalıyken bütün yazılar "bulunamadı" görünürdü —
 * arama motoru da onları silinmiş sanıp dizinden düşürürdü.
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
